import { BaseWebComponentView } from '../../../../../framework/BaseWebComponentView';
import {
  ColumnKeys,
  GRID_COLUMNS,
  IntegrityMonitorPolicyRuleSetsSectionWebComponentSchema,
  PolicyRuleSetsSectionEvents,
  RuleSetItem,
} from '../types';
import { EnumListItem } from '../../../../../ui/gzEnumList/types';
import { IntegrityMonitorPolicyRuleSetsGridDataProvider } from '../dataProvider/IntegrityMonitorPolicyRuleSetsGridDataProvider';
import { TemplateState } from '../../../../../types/common/types';
import { IntegrityMonitorRuleSets } from '../../../../endpoints/policyPanel/uiSettings/integrityMonitor/ruleSetsSection/utils/helper';

export class IntegrityMonitorPolicyRuleSetsSectionView extends BaseWebComponentView {
  getDomElements() {
    this.dom = {
      $wrapper: this.shadow.querySelector('.wrapper'),
      $ruleSetsSelector: this.shadow.querySelector('#rule-sets-selector'),
      $grid: this.shadow.querySelector('#rule-sets-grid'),
    };

    this.getTranslations();
    this.setTranslatedStrings();
    this.injectDependenciesInChildren();
  }

  getTranslations() {
    this.commonStrings = this.stringsTranslator.getCommonStrings();
    this.translations = this.stringsTranslator.getIntegrityMonitorStrings().policies.realTime;
  }

  setTranslatedStrings() {
    this.dom.$ruleSetsSelector.placeholder = this.translations.select;
    this.dom.$ruleSetsSelector.searchPlaceholderString = this.translations.search;
    this.dom.$ruleSetsSelector.confirmActionButtonString = this.commonStrings.apply;
    this.dom.$ruleSetsSelector.noSearchResultsString = this.translations.noRuleSetsFound;
    this.dom.$ruleSetsSelector.errorMessage = this.translations.errorLoadRuleSets;
    this.dom.$ruleSetsSelector.allItemsLabel = this.translations.allRuleSets;
  }

  buildSuccessView(data: IntegrityMonitorPolicyRuleSetsSectionWebComponentSchema) {
    this.buildRuleSetsSelector(data);
    this.buildGrid(data);
  }

  buildErrorView(error: Error) {
    console.error(error);
    this.resetTemplateState(this.dom.$wrapper);
    this.setTemplateState(this.dom.$wrapper, TemplateState.ERROR);
  }

  public setDisabled(disabled: boolean) {
    this.dom.$ruleSetsSelector.disabled = disabled;
    this.dom.$grid.disabled = disabled;
    this.dom.$grid.controller.render();
  }

  private injectDependenciesInChildren() {
    if (this.dom.$grid.injectDependencies) {
      this.dom.$grid.injectDependencies(this.dependencies);
      this.dom.$grid.start().catch(console.error);
    }
  }

  private buildRuleSetsSelector(data: IntegrityMonitorPolicyRuleSetsSectionWebComponentSchema) {
    const selectedRuleSetIds = data.selectedRuleSets.map(ruleSet => ruleSet._id);

    this.dom.$ruleSetsSelector.disabled = data.isDisabled();
    this.dom.$ruleSetsSelector.filterOptions = data.companyRuleSets
      .map(ruleSet => {
        const isSelectedAndDisabled = selectedRuleSetIds.includes(ruleSet._id);
        return {
          label: ruleSet.name,
          value: ruleSet._id,
          selected: isSelectedAndDisabled,
          disabled: isSelectedAndDisabled,
        } as EnumListItem;
      })
      .sort((ruleSet1: EnumListItem, ruleSet2: EnumListItem) => {
        const name1 = ruleSet1.label.toLocaleLowerCase();
        const name2 = ruleSet2.label.toLocaleLowerCase();

        return name1 === name2 ? 0 : name1 > name2 ? 1 : -1;
      });
  }

  private buildGrid(data: IntegrityMonitorPolicyRuleSetsSectionWebComponentSchema) {
    this.dom.$grid.columns = GRID_COLUMNS;
    this.dom.$grid.viewId = 'integrity-monitor-rule-sets-view';
    this.dom.$grid.allColumnDetails = IntegrityMonitorRuleSets.getGridColumnDetails({
      ruleSet: this.translations.ruleSet,
      rules: this.translations.rules,
      actions: '',
    });
    this.dom.$grid.filters = [];
    this.dom.$grid.isCompactViewChecked = true;
    this.dom.$grid.removeFilters = true;
    this.dom.$grid.hideHeaderActions = true;
    this.dom.$grid.hideFooter = true;
    this.dom.$grid.dataProvider = new IntegrityMonitorPolicyRuleSetsGridDataProvider(data.selectedRuleSets);
    this.dom.$grid.columnRenderers = {
      [ColumnKeys.ACTIONS]: this.renderActionsCell.bind(this, data.isDisabled),
    };
    this.dom.$grid.customStrings = {
      noSearchResultsString: this.translations.noRuleSetsSelected,
    };

    this.dom.$grid.controller.render();
  }

  private renderActionsCell(isDisabled: () => boolean, rowData: { item: RuleSetItem }): DocumentFragment | null {
    const templateElement = document.createElement('template');

    templateElement.innerHTML = `<span class="d-flex align-items-center justify-end" style="width: 100%">
      <gz-icon-button
        icon="ds-icon-cancel"
        buttoncolor="secondary"
        fontsize="20"
        buttontype="round"
        buttonfill="quiet"
        size="24"
      ></gz-icon-button>
    </span>`;

    const buttonElement = templateElement.content.querySelector('gz-icon-button');

    if (isDisabled()) {
      buttonElement?.setAttribute('disabled', '');
    } else {
      buttonElement?.addEventListener('click', () => {
        this.dom.$grid.dispatchEvent(
          new CustomEvent(PolicyRuleSetsSectionEvents.REMOVE_RULE_SET, {
            detail: {
              ruleSet: rowData.item,
            },
            bubbles: true,
            cancelable: true,
          })
        );
      });
    }

    return templateElement.content;
  }
}
